import {
  Box,
  Center,
  Flex,
  Heading,
  Icon,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback } from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';

import { finleyAssert } from '@finley-technologies/common';
import { mutateWipMetadata } from '@finley-technologies/credit-agreement-utils';

import {
  errorToastAdminConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { truncateUploadFileName } from 'packages/common-frontend/utils';
import { FiFilePlus, FiFileMinus } from 'react-icons/fi';

type UploadJsonDropzoneProps = {
  setJSON: (values: React.SetStateAction<any>) => void;
  currentCreditAgreementId: string;
};
export const UploadJsonDropzone = ({
  setJSON,
  currentCreditAgreementId,
}: UploadJsonDropzoneProps) => {
  const toast = useToast();
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: { 'application/json': ['.json'] },
    multiple: false,
    onDrop: useCallback(
      (files: FileWithPath[]) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            finleyAssert(
              _.isString(reader.result),
              `reader.result, Expected string [Found:${typeof reader.result}]`,
            );
            const wip = JSON.parse(reader.result);

            setJSON(mutateWipMetadata({ wip, currentCreditAgreementId }));
          } catch {
            if (!toast.isActive(errorToastId)) {
              errorToastAdminConfig.title = `Error parsing JSON.`;
              toast(errorToastAdminConfig);
            }
          }
        };
        reader.readAsText(files[0], 'utf-8');
      },
      [setJSON, toast, currentCreditAgreementId],
    ),
  });

  const acceptedFileItems = _.map(acceptedFiles, (file: FileWithPath) => (
    <Text key={file.path}>{truncateUploadFileName(file.path)}</Text>
  ));

  const fileRejectionItems = _.map(
    fileRejections,
    ({ file }: { file: FileWithPath }) => (
      <>
        <Text key={file.path}>{truncateUploadFileName(file.path)}</Text>
        <Text>
          Expected file of type <em>json</em>
        </Text>
      </>
    ),
  );

  const fileSelected = !_.isEmpty(acceptedFiles) || !_.isEmpty(fileRejections);

  return (
    <Box width="100%" cursor="pointer">
      <Center
        border="1px"
        borderRadius="md"
        minHeight="200px"
        flexDirection="column"
        {...getRootProps({ className: 'dropzone' })}
      >
        <input {...getInputProps()} />
        {!_.isEmpty(acceptedFiles) && (
          <>
            <Heading size="sm">
              <Icon as={FiFilePlus} color="green.300" /> Accepted
            </Heading>
            {acceptedFileItems}
          </>
        )}
        {!_.isEmpty(fileRejections) && (
          <>
            <Flex alignItems="center">
              <Icon as={FiFileMinus} color="red.300" />
              <Heading size="sm">Rejected</Heading>
            </Flex>
            {fileRejectionItems}
          </>
        )}
        <Text
          textAlign="center"
          as={fileSelected ? 'sub' : 'i'}
          pt={fileSelected ? '20px' : '0'}
        >
          {`Drag a file here, or click to select${
            fileSelected ? ' a new' : ''
          } file`}
        </Text>
      </Center>
    </Box>
  );
};
